import React, { useCallback, useEffect } from 'react';
import { ReactLenis, useLenis } from 'lenis/react';
import { useReducedMotion } from 'motion/react';

// Header height plus breathing room, so anchored sections don't hide under the nav
export const ANCHOR_OFFSET = -96;

/*
 * Lenis smooth scrolling for the whole page (native scroll with interpolated wheel input,
 * so position: sticky and IntersectionObserver keep working). In-page #anchor links glide;
 * reduced-motion visitors get plain native scrolling.
 */
const SmoothScroll = ({ children }) => {
  const reduced = useReducedMotion();
  if (reduced) return children;

  return (
    <ReactLenis
      root
      options={{
        lerp: 0.11,
        smoothWheel: true,
        anchors: { offset: ANCHOR_OFFSET },
        stopInertiaOnNavigate: true,
        prevent: node => node.hasAttribute?.('data-lenis-prevent')
      }}
    >
      {children}
    </ReactLenis>
  );
};

/*
 * Hold the page still while `active` (Lenis paused as well as native scroll, since Lenis
 * scrolls programmatically). Several things can hold it at once (the preloader, the mobile
 * menu, the landing page's gate and worlds), so holds are counted: the page scrolls again
 * only when the last one lets go, and one component letting go can't release another's.
 */
const holds = new Set();
const applyHolds = lenis => {
  const held = holds.size > 0;
  document.documentElement.style.overflow = held ? 'hidden' : '';
  if (held) lenis?.stop();
  else lenis?.start();
};

export const useScrollLock = active => {
  const lenis = useLenis();
  useEffect(() => {
    if (!active) return undefined;
    const hold = {};
    holds.add(hold);
    applyHolds(lenis);
    return () => {
      holds.delete(hold);
      applyHolds(lenis);
    };
  }, [active, lenis]);
};

// Scroll helper that uses Lenis when it's running and falls back to native scrolling
export const useScrollTo = () => {
  const lenis = useLenis();
  return useCallback(
    (target, { immediate = false, offset = ANCHOR_OFFSET } = {}) => {
      if (lenis) {
        lenis.scrollTo(target, { immediate, offset: typeof target === 'number' ? 0 : offset, force: true });
        return;
      }
      if (typeof target === 'number') {
        window.scrollTo({ top: target, behavior: immediate ? 'instant' : 'smooth' });
      } else if (target) {
        const top = target.getBoundingClientRect().top + window.scrollY + offset;
        window.scrollTo({ top, behavior: immediate ? 'instant' : 'smooth' });
      }
    },
    [lenis]
  );
};

export default SmoothScroll;
